import { useState } from 'react';
import { t } from '@/i18n/vi';
import { formatUserError, isAuthRequiredError } from '@/lib/errors';
import { AuthRequired } from './AuthRequired';

export function PageSkeleton({ message = t.loading }: { message?: string }) {
  return (
    <div className="flex items-center justify-center min-h-screen p-8">
      <div className="flex flex-col items-center gap-3 text-slate-500">
        <div
          className="h-10 w-10 rounded-full border-4 border-slate-200 border-t-brand-600 animate-spin"
          aria-hidden
        />
        <p className="text-sm font-medium">{message}</p>
      </div>
    </div>
  );
}

/**
 * Route errorComponent for content routes: shows the friendly sign-in screen when the read
 * was denied for an anonymous user, otherwise the standard error card.
 */
export function ContentRouteError({ error, reset }: { error: unknown; reset: () => void }) {
  if (isAuthRequiredError(error)) return <AuthRequired />;
  return <PageError error={error} onRetry={reset} />;
}

interface PageErrorProps {
  error: unknown;
  onRetry?: () => void;
  onHome?: () => void;
}

/**
 * Full-page error display for route loaders.
 * Translates raw errors via formatUserError — never renders raw library messages.
 */
export function PageError({ error, onRetry, onHome }: PageErrorProps) {
  const info = formatUserError(error);
  const [retrying, setRetrying] = useState(false);

  const handleRetry = () => {
    if (retrying) return;
    setRetrying(true);
    requestAnimationFrame(() => {
      // Offline errors: full page reload so browser loading bar gives clear feedback.
      // Other retryable errors: router-level reset re-runs the loader.
      if (info.code === 'offline') {
        window.location.reload();
      } else {
        onRetry?.();
      }
    });
  };

  return (
    <div className="flex items-center justify-center min-h-screen p-8">
      <div
        role="alert"
        aria-live="assertive"
        className="max-w-md w-full rounded-xl p-6 text-center"
        style={{
          background: 'var(--wrong-bg)',
          border: '1px solid rgba(220,38,38,0.25)',
        }}
      >
        <h2 className="text-lg font-semibold" style={{ color: 'var(--wrong)' }}>
          {info.title}
        </h2>
        <p className="text-sm mt-2 break-words" style={{ color: 'var(--wrong)' }}>
          {info.detail}
        </p>
        {(info.canRetry) || onHome ? (
          <div className="mt-5 flex flex-col items-stretch gap-3">
            {info.canRetry && (
              <button
                type="button"
                onClick={handleRetry}
                disabled={retrying}
                className="flex items-center justify-center gap-2 w-full rounded-xl py-3 text-base font-semibold text-white cursor-pointer disabled:opacity-70"
                style={{ background: 'var(--wrong)', border: 'none', minHeight: 48 }}
              >
                {retrying ? (
                  <>
                    <span
                      className="inline-block h-4 w-4 rounded-full border-2 border-white border-t-transparent animate-spin"
                      aria-hidden
                    />
                    Đang thử lại…
                  </>
                ) : (
                  t.errorRetry
                )}
              </button>
            )}
            {onHome && (
              <button
                type="button"
                onClick={onHome}
                className="flex items-center justify-center w-full rounded-xl py-3 text-sm font-medium cursor-pointer"
                style={{
                  background: 'transparent',
                  color: 'var(--wrong)',
                  border: '1px solid rgba(220,38,38,0.35)',
                  minHeight: 44,
                }}
              >
                {t.errorGoHome}
              </button>
            )}
          </div>
        ) : null}
      </div>
    </div>
  );
}
